import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import styles from "../../../styles/components/Modal/LeadSettings/BulkUpdateModal.module.css";

import { Notification } from '../../UI/Notifications';
import { LangContext } from '../../../context/LangContext';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { handleError } from '../../../utils/custom/errorHandler';

import { bulkUpdateLeadsApi } from '../../../utils/api/Lead/bulkUpdateLeadsApi';
import { getAdmins } from '../../../utils/api/Admin/getAdminsApi';
import { getAllDesksApi } from '../../../utils/api/Admin/desk/getAllDesksApi';
import { getAffiliate } from '../../../utils/api/Affiliate/getAffiliateApi';
import { fetchFilterOptions } from '../../../utils/api/fetchLeadFilter';

import { fieldLabels } from '../../../utils/forms/fieldsLabels';

const translations = {
    ru: {
        title: (count) => `Массовое изменение (${count} лидов)`,
        save: 'Сохранить изменения',
        cancel: 'Отмена',
        updating: 'Обновление...',
        omit: 'Не изменять',
        optional: ' (Опционально)',
        success: (count) => `${count} лидов успешно обновлены.`,
        error: 'Произошла ошибка при массовом обновлении лидов.',
        loadingOptions: 'Загрузка опций...',
        noOptions: 'Нет доступных опций',
    },
    en: {
        title: (count) => `Bulk Update (${count} leads)`,
        save: 'Save Changes',
        cancel: 'Cancel',
        updating: 'Updating...',
        omit: 'Do not change',
        optional: ' (Optional)',
        success: (count) => `${count} leads successfully updated.`,
        error: 'An error occurred during bulk lead update.',
        loadingOptions: 'Loading options...',
        noOptions: 'No options available',
    },
};

const UPDATE_FIELDS_CONFIG = [
    { key: 'manager_id', type: 'admin', labelKey: 'manager_id', endpoint: null, formKey: 'manager_id' },
    { key: 'sale_status', type: 'dropdown', labelKey: 'sale_status', endpoint: '/common/lead/filters/sale-statuses', formKey: 'sale_status' },
    { key: 'reten_status', type: 'dropdown', labelKey: 'reten_status', endpoint: '/common/lead/filters/retention-statuses', formKey: 'reten_status' },
    { key: 'desk_id', type: 'desk', labelKey: 'desk_id', endpoint: null, formKey: 'desk_id' },
    { key: 'affiliator_id', type: 'affiliator', labelKey: 'affiliator_id', endpoint: null, formKey: 'affiliator_id' },
];

export const BulkUpdateModal = ({ leadIds, onClose, onSuccess }) => {
    const { langSelected } = useContext(LangContext);
    const t = translations[langSelected] || translations.ru;
    const tFields = fieldLabels[langSelected] || fieldLabels.ru;

    const modalRef = useRef(null);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dropdownOptions, setDropdownOptions] = useState({});
    const [isOptionsLoading, setIsOptionsLoading] = useState(true);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const loadOptions = useCallback(async () => {
        setIsOptionsLoading(true);
        const options = {};
        try {
            const results = await Promise.all(
                UPDATE_FIELDS_CONFIG.map(async (field) => {
                    let data = [];

                    if (field.type === 'admin') {
                        const admins = await getAdmins();
                        data = (admins.data || []).map(admin => ({
                            value: admin.id,
                            label: admin.first_name ? `${admin.first_name} ${admin.last_name || ''}` : admin.email,
                        }));
                    } else if (field.type === 'desk') {
                        const desksData = await getAllDesksApi();
                        data = (desksData.data || []).map(desk => ({
                            value: desk.id,
                            label: desk.name,
                        }));
                    } else if (field.type === 'affiliator') {
                        let allAffiliators = [];
                        let currentPage = 1;
                        let totalPages = 1;
                        const maxPerPage = 50;

                        do {
                            const result = await getAffiliate(currentPage, maxPerPage);
                            allAffiliators = allAffiliators.concat(result.data || []);
                            totalPages = result.meta.last_page || 1;
                            currentPage++;
                        } while (currentPage <= totalPages);

                        data = allAffiliators.map(affiliator => ({
                            value: affiliator.id,
                            label: affiliator.affiliate || (affiliator.first_name ? `${affiliator.first_name} ${affiliator.last_name || ''}` : affiliator.email),
                        }));

                    } else if (field.endpoint) {
                        const result = await fetchFilterOptions(field.endpoint);


                        const rawData = result && result.data ? result.data : result;
                        const arrayData = Array.isArray(rawData) ? rawData : [];


                        data = arrayData.map(item => {
                            if (typeof item === 'string') {
                                return { value: item, label: item };
                            }

                            return { value: item.value, label: item.label };
                        });
                    }
                    return { key: field.key, options: data };
                })
            );

            results.forEach(result => {
                options[result.key] = [{ value: '', label: t.omit }, ...result.options];
            });

        } catch (error) {
            console.error(t.error, error);
            setErrors({ general: t.error + (error.message || '') });
            onClose();
        } finally {
            setDropdownOptions(options);
            setIsOptionsLoading(false);
        }
    }, [t, leadIds.length, onClose]);

    useEffect(() => {
        if (leadIds.length > 0) {
            loadOptions();
        }
    }, [leadIds, loadOptions]);

    const handleFieldChange = useCallback((filterName, value) => {
        setFormData(prev => {
            const newForm = { ...prev };
            if (value === '') {
                delete newForm[filterName];
            } else {
                newForm[filterName] = value;
            }
            return newForm;
        });
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (Object.keys(formData).length === 0) {
            setErrors({ general: 'Выберите хотя бы одно поле для изменения.' });
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        try {
            const dataToSubmit = {
                lead_ids: leadIds,
                ...formData
            };

            await bulkUpdateLeadsApi(dataToSubmit);

            onSuccess();
            onClose();

        } catch (err) {
            handleError(err, setErrors);
            setErrors({ general: err.message || t.error });
        } finally {
            setIsSubmitting(false);
        }
    };



    if (isOptionsLoading) {
        return (
            <div className={styles.modalOverlay}>
                <div className={styles.loadingContainer}>
                    <CircularProgress size={30} />
                    <p>{t.loadingOptions}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.filtersContainer} ref={modalRef}>
                <div className={styles.modalLabel}>
                    <p>{t.title(leadIds.length)}</p>
                    <div onClick={onClose}><CloseIcon /></div>
                </div>

                {errors.general && <Notification message={errors.general} type="error" />}

                <form onSubmit={handleSubmit}>
                    <div className={styles.modalContent}>

                        {UPDATE_FIELDS_CONFIG.map((field) => {
                            const options = dropdownOptions[field.key] || [];

                            const fieldLabel = tFields[field.labelKey] || field.labelKey;

                            return (
                                <div key={field.key} className={styles.inputRow}>
                                    <label>{fieldLabel}{t.optional}:</label>
                                    <DropdownSelector
                                        filterName={field.key}
                                        selectedValue={formData[field.key] || ''}
                                        handleFilterChange={handleFieldChange}
                                        options={options}
                                        hasError={false}
                                        errorMessage={''}
                                        disabled={isSubmitting || options.length === 1}
                                    />
                                </div>
                            );
                        })}

                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className={styles.clearButton}
                        >
                            {t.cancel}
                        </button>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isSubmitting || Object.keys(formData).length === 0}
                        >
                            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t.save}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};